import React, { useState, useEffect } from "react";
import styles from "./all-teachers-tab.module.css";
import { Avatar, Button, Dropdown, Menu, Popconfirm, Tooltip, Typography } from "antd";
import { MoreOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import CustomTextfield from "../../../../common/input/custom-textfield/custom-textfield";
import SVGIcon from "../../../../constants/SVGIcon";
import CustomTable from "../../../../common/custom-table/custom-table";
import RoutesConstant from "../../../../constants/routes";
import CustomButton from "../../../../common/custom-button/custom-button";
import { toast } from "react-toastify";
import { deleteTeacher, getTeachersList } from "../../../../services/teachers";
import { resendCredentialsTeachers } from "../../../../services/teachers";
const AllTeachersList = ({ triggerRefetch }) => {
  const navigate = useNavigate();
  const [teachers, setTeachers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    const timeOut = setTimeout(() => {
      setPageNo(1);
      fetchTeachers();
    }, 500);
    return () => {
      clearTimeout(timeOut);
    };
  }, [searchTerm, triggerRefetch]);

  const fetchTeachers = async (tempPage = pageNo, tempPageSize = pageSize) => {
    try {
      setLoading(true);
      const payload = {
        page: tempPage,
        pageSize: tempPageSize,
      };

      if (searchTerm) {
        payload.search = searchTerm;
      }

      const resp = await getTeachersList(payload);
      if (resp?.data) {
        const data = resp?.data?.data?.teachers;
        setTeachers(data);
        setTotalCount(resp?.data?.data?.totalRecords);
      }
      setLoading(false);
    } catch (err) {
      console.log(err);
      toast.error("Error Fetching Teachers");
      setLoading(false);
    }
  };

  const handleChangePage = (page, pageSize) => {
    setPageNo(page);
    setPageSize(pageSize);
    fetchTeachers(page, pageSize);
  };

  const handleDeleteTeacher = async (teacherId) => {
    try {
      setLoading(true);
      await deleteTeacher(teacherId);
      await fetchTeachers();
      toast.success("Teacher is deleted");
    } catch (err) {
      toast.error("Failed to delete teacher");
    } finally {
      setLoading(false);
    }
  };
  const handleResendCredentials = async (teacherId) => {
    try {
      setLoading(true);
      await resendCredentialsTeachers({
        teacher_id: teacherId,
        classroom_id: "",
      });
      toast.success("Resend is Successful");
    } catch (err) {
      toast.success("Error in Resending Credentials");
    } finally {
      setLoading(false);
    }
  };
  const handleResendAllCredentials = async () => {
    try {
      setLoading(true);
      await resendCredentialsTeachers({ teacher_id: "", classroom_id: "" });
      toast.success("ResendAll is Successful");
    } catch (err) {
      toast.success("Error in ResendingAll Credentials");
    } finally {
      setLoading(false);
    }
  };

  const getUniqueValues = (arr, key) =>
    Array.from(new Set(arr?.map(item => item[key])));

  const columns = [
    { title: "Emp ID.", dataIndex: "user_name", key: "user_name" },
    {
      title: "Teacher Name",
      dataIndex: "full_name",
      key: "full_name",
      render: (text, record) => (
        <div style={{ display: "flex", alignItems: "center" }}>
          <Avatar
            src={record.profile_picture}
            style={{ marginRight: 8 }}
            size={40}
          >
            {record.full_name?.[0]}
          </Avatar>
          {record.full_name}
        </div>
      ),
    },
    { title: "Gender", dataIndex: "gender", key: "gender" },
    {
      title: "Expert In",
      dataIndex: "subjects_taught",
      key: "subjects_taught",
      render: (value = []) => {
        const uniqueSubjects = getUniqueValues(value, "subject_name");
        const extraSubjects = uniqueSubjects?.length > 1 ? uniqueSubjects?.slice(1) : [];

        return (
          <div className={styles.subjectsValue}>
            {uniqueSubjects[0] ?? "-"}
            {extraSubjects?.length > 0 && (
              <Tooltip 
                title={extraSubjects?.join(", ")}
                color="var(--primary-color)"
              >
                <span className={styles.subjectMoreTooltip}>
                  +{extraSubjects?.length}
                </span>
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      title: "Department",
      dataIndex: "subjects_taught",
      key: "subjects_taught_department",
      render: (value = []) => {
        const uniqueDepartments = getUniqueValues(value, "department_name");
        const extraDepartments = uniqueDepartments?.length > 1 ? uniqueDepartments?.slice(1) : [];

        return (
          <div className={styles.subjectsValue}>
            {uniqueDepartments[0] ?? "-"}
            {extraDepartments?.length > 0 && (
              <Tooltip 
                title={extraDepartments?.join(", ")}
                color="var(--primary-color)"
              >
                <span className={styles.subjectMoreTooltip}>
                  +{extraDepartments?.length}
                </span>
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      title: "Experience",
      dataIndex: "years_of_experience",
      key: "years_of_experience",
      render: (value) => <p>{value ? value + " years" : "-"}</p>,
    },
    {
      title: "",
      dataIndex: "actions",
      key: "actions",
      width: 50,
      align: "center",
      render: (_, record) => {
        return (
          <Dropdown
            overlay={<Menu items={menuItems(record)} />}
            trigger={["click"]}
          >
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        );
      },
    },
  ];

  const menuItems = (record) => [
    {
      key: "1",
      label: "View Details",
      onClick: () =>
        navigate(`${RoutesConstant.TEACHER_DETAILS}?id=${record?.id}`),
    },
    {
      key: "2",
      label: "Edit",
      onClick: () =>
        navigate(`${RoutesConstant.TEACHER_FORM}?uid=${record?.id}`),
    },
    {
      key: "3",
      label: (
        <Popconfirm
          title={`Are you sure you want to delete ${record?.full_name}?`}
          onConfirm={() => handleDeleteTeacher(record?.id)}
          okText="Yes"
          cancelText="No"
        >
          <span>Delete</span>
        </Popconfirm>
      ),
    },
    {
      key: "4",
      label: (
        <Popconfirm
          title={`Are you sure you want to send credentials to ${record?.full_name}?`}
          onConfirm={() => handleResendCredentials(record?.id)}
          okText="Yes"
          cancelText="No"
        >
          <span>Resend Credentials</span>
        </Popconfirm>
      ),
    },
  ];

  return (
    <div className={styles.main_container}>
      <div className={styles.title_container}>
        <Typography className={styles.title}>All Teachers</Typography>
        <div className={styles.filter_container}>
          <CustomButton
            title="Resend All Credentials"
            onClick={() => {
              handleResendAllCredentials();
            }}
          />
          <CustomTextfield
            id={"search"}
            type={"text"}
            className={styles.inputField}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
            suffix={
              <SVGIcon
                name={"search-icon"}
                width={"24"}
                height={"24"}
                className="anticon"
                stroke={"#667085"}
              />
            }
            placeholder={"Search ..."}
          />
        </div>
      </div>
      <CustomTable
        columns={columns}
        dataSource={teachers}
        loading={loading}
        total={totalCount}
        pageSize={pageSize}
        currentPage={pageNo}
        handlePageChange={handleChangePage}
      />
    </div>
  );
};

export default AllTeachersList;
